/**
 * Thin fetch wrapper used by the tenant console SPAs.
 *
 * Responsibilities:
 *   - Attach `credentials: 'include'` so the tenant session cookie round-trips.
 *   - Default JSON content-type on bodied requests.
 *   - Normalize non-2xx responses into `PlatformApiError`.
 *   - Forward network-level failures as a `SERVICE_UNAVAILABLE`-shaped error.
 *
 * Base URLs are resolved from app-specific env vars first, then shared env
 * vars, then known production/staging/dev hostnames, and finally local worker
 * ports for Vite development.
 */

const LOCAL_PLATFORM_API_PORT = 8787;
const LOCAL_GATEWAY_PORT = 8786;
const LOCAL_HOST_PARTS = ['local', 'host'] as const;

export function getGatewayBaseUrl(): string {
  const fromEnv = normalizeEnvUrl(import.meta.env.VITE_GATEWAY_BASE_URL);
  const fromNxtpathEnv = normalizeEnvUrl(import.meta.env.VITE_NXTPATH_GATEWAY_BASE_URL);
  const inferred = inferPublicApiBaseUrl('api');
  if (shouldUseNxtpathEndpoints()) {
    return fromNxtpathEnv ?? inferred ?? fromEnv ?? localWorkerBaseUrl(LOCAL_GATEWAY_PORT);
  }
  if (fromEnv !== null) {
    return fromEnv;
  }
  if (inferred !== null) {
    return inferred;
  }
  if (fromNxtpathEnv !== null) {
    return fromNxtpathEnv;
  }
  return localWorkerBaseUrl(LOCAL_GATEWAY_PORT);
}

interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    retryable: boolean;
  };
}

export class PlatformApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;
  /** Parsed response body, retained for endpoint-specific structured errors. */
  readonly responseBody: unknown;
  constructor(
    status: number,
    code: string,
    message: string,
    retryable: boolean,
    responseBody: unknown = null,
  ) {
    super(message);
    this.name = 'PlatformApiError';
    this.status = status;
    this.code = code;
    this.retryable = retryable;
    this.responseBody = responseBody;
  }
}

export interface PlatformApiRequestInit {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export function getPlatformApiBaseUrl(): string {
  const fromEnv = normalizeEnvUrl(import.meta.env.VITE_PLATFORM_API_BASE_URL);
  const fromNxtpathEnv = normalizeEnvUrl(import.meta.env.VITE_NXTPATH_PLATFORM_API_BASE_URL);
  const inferred = inferPublicApiBaseUrl('platform');
  if (shouldUseNxtpathEndpoints()) {
    return fromNxtpathEnv ?? inferred ?? fromEnv ?? localWorkerBaseUrl(LOCAL_PLATFORM_API_PORT);
  }
  if (fromEnv !== null) {
    return fromEnv;
  }
  if (inferred !== null) {
    return inferred;
  }
  if (fromNxtpathEnv !== null) {
    return fromNxtpathEnv;
  }
  return localWorkerBaseUrl(LOCAL_PLATFORM_API_PORT);
}

/**
 * Issue a request against platform-api. Returns parsed JSON body typed by the
 * caller. Throws `PlatformApiError` on any non-2xx.
 */
export async function platformApiRequest<T>(
  path: string,
  init: PlatformApiRequestInit = {},
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const baseUrl = getPlatformApiBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${baseUrl}${normalizedPath}`;

  const headers: Record<string, string> = { ...(init.headers ?? {}) };
  let body: BodyInit | undefined;
  if (init.body !== undefined) {
    body = JSON.stringify(init.body);
    if (headers['Content-Type'] === undefined && headers['content-type'] === undefined) {
      headers['Content-Type'] = 'application/json';
    }
  }

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: init.method ?? (body === undefined ? 'GET' : 'POST'),
      credentials: 'include',
      headers,
      body,
      signal: init.signal,
    });
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'network request failed';
    throw new PlatformApiError(0, 'SERVICE_UNAVAILABLE', message, true);
  }

  const rawText = await response.text();
  const parsed = rawText.length === 0 ? null : safeJsonParse(rawText);

  if (!response.ok) {
    const envelope = asErrorEnvelope(parsed);
    if (envelope !== null) {
      throw new PlatformApiError(
        response.status,
        envelope.error.code,
        envelope.error.message,
        envelope.error.retryable,
        parsed,
      );
    }
    throw new PlatformApiError(
      response.status,
      'SERVICE_UNAVAILABLE',
      `platform-api returned ${response.status} with non-envelope body`,
      response.status >= 500,
      parsed,
    );
  }

  return parsed as T;
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function asErrorEnvelope(raw: unknown): ErrorEnvelope | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const error = (raw as { error?: unknown }).error;
  if (error === null || typeof error !== 'object') {
    return null;
  }
  const code = (error as { code?: unknown }).code;
  const message = (error as { message?: unknown }).message;
  const retryable = (error as { retryable?: unknown }).retryable;
  if (typeof code !== 'string' || typeof message !== 'string' || typeof retryable !== 'boolean') {
    return null;
  }
  return { error: { code, message, retryable } };
}

function normalizeEnvUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }
  return value.replace(/\/+$/, '');
}

function shouldUseNxtpathEndpoints(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = getWindowHostname();
  if (host === null) {
    return false;
  }
  return host.endsWith('.nxtpath.ai') || /^nxtpath-console-web-[^.]+\.pages\.dev$/.test(host);
}

function inferPublicApiBaseUrl(service: 'api' | 'platform'): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const host = getWindowHostname();
  if (host === null) {
    return null;
  }
  if (host === 'app.nadoutong.org') {
    return `https://${service}.nadoutong.org`;
  }
  if (host.startsWith('app.') && host.endsWith('.nadoutong.org')) {
    return `https://${service}.${host.slice('app.'.length)}`;
  }
  if (host === 'app.nxtpath.ai') {
    return service === 'api'
      ? 'https://api.nxtpath.ai'
      : 'https://platform.nxtpath.ai';
  }
  if (host === 'app.staging.nxtpath.ai') {
    return service === 'api'
      ? 'https://api.staging.nxtpath.ai'
      : 'https://platform.staging.nxtpath.ai';
  }
  if (host === 'app.dev.nxtpath.ai') {
    return service === 'api'
      ? 'https://api.dev.nxtpath.ai'
      : 'https://platform.dev.nxtpath.ai';
  }
  if (host === 'nxtpath-console-web-production.pages.dev') {
    return service === 'api'
      ? 'https://api.nxtpath.ai'
      : 'https://platform.nadoutong.org';
  }
  if (host === 'nxtpath-console-web-staging.pages.dev') {
    return service === 'api'
      ? 'https://api.staging.nxtpath.ai'
      : 'https://platform.staging.nadoutong.org';
  }
  if (host === 'nxtpath-console-web-dev.pages.dev') {
    return service === 'api'
      ? 'https://api.dev.nxtpath.ai'
      : 'https://platform.dev.nadoutong.org';
  }
  return null;
}

function localWorkerBaseUrl(port: number): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  const host = getWindowHostname() ?? LOCAL_HOST_PARTS.join('');
  return `${protocol}//${host}:${port}`;
}

function getWindowHostname(): string | null {
  const hostname = window.location.hostname;
  return typeof hostname === 'string' && hostname.length > 0 ? hostname : null;
}
